import React, { Component } from "react";
import axios from "axios";
import Auth from "../../Auth";
import Loading from "../loading";
import { link, downloadFile, numb2 } from "../link.js";
import {
  FIRST_PAGE,
  PAGE_STEP,
  toNumber,
  matchesSearch,
  ReportLayout,
  ReportCard,
  ReportFilters,
  ReportField,
  RoundSelect,
  SearchField,
  ReportSummary,
  ReportEmpty,
  ReportTable,
  ReportFooter,
} from "./reportParts";

const STANDARD_COLUMNS = [
  { label: "#" },
  { label: "ID" },
  { label: "Month/Date" },
  { label: "PARTY" },
  { label: "Category" },
  { label: "Type" },
  { label: "Description" },
  { label: "Curr" },
  { label: "Paid date" },
  { label: "Total to pay", numeric: true },
  { label: "Paid", numeric: true },
  { label: "Balance", numeric: true },
];

const BALANCE_COLUMNS = [
  { label: "#" },
  { label: "ID" },
  { label: "Month/Date" },
  { label: "PARTY" },
  { label: "Category" },
  { label: "Type" },
  { label: "Description" },
  { label: "Curr" },
  { label: "Payable", numeric: true },
  { label: "Other", numeric: true },
];

const withParty = (columns, party) => columns.map((column) => (column.label === "PARTY" ? { ...column, label: party } : column));

/**
 * Income and expense reports share one flow: pick a round and a report, the backend returns the ledger lines and
 * totals, the payer/payee search narrows them in the browser. `balanceTypes` are the reports that list what is
 * still payable or receivable instead of amounts to pay and paid.
 */
export class LedgerReport extends Component {
  constructor(props) {
    super(props);
    this.state = {
      rounds: [],
      report: null,
      reportType: props.defaultType,
      filters: { roundid: "", name: "" },
      loading: true,
      dataOut: FIRST_PAGE,
    };
    this.mounted = false;
    this.requestId = 0;
  }

  componentDidMount() {
    this.mounted = true;
    this.getRounds();
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  getRounds = () => {
    fetch(link() + `&do=getshort&ra=round&ft=areclist&elmt=roundStatus&id=Active`, {
      method: "post",
      headers: { "Content-Type": "application/json" },
    })
      .then((response) => response.json())
      .then((response) => {
        if (!this.mounted) return;
        this.setState({ rounds: Array.isArray(response) ? response : [] });
        this.getReport(this.state.reportType, this.state.filters);
      })
      .catch((err) => {
        console.log("Error: ", err);
        Auth.handleHttpCodes(err, this.props.name);
        if (this.mounted) this.setState({ loading: false });
      });
  };

  requestBody = (filters) => ({
    datefrom: "",
    dateTo: "",
    userid: "",
    memberid: "",
    roundid: filters.roundid,
    roundname: "",
    name: filters.name,
    termstartdate: "",
    termduedate: "",
    status: "",
  });

  getReport = (reportType, filters) => {
    const request = ++this.requestId;
    this.setState({ loading: true, reportType });
    localStorage.setItem("currentView", "income");
    axios
      .post(link() + `&do=get&ra=report&rec=${reportType}`, this.requestBody(filters), {
        headers: { "Content-Type": "application/json" },
        timeout: 300000,
      })
      .then((res) => {
        if (!this.mounted || request !== this.requestId) return;
        const report = res.data && typeof res.data === "object" ? res.data : {};
        // With no round chosen the backend reports on the company's current round and says which one it is.
        this.setState({
          report,
          filters: { ...this.state.filters, roundid: report.roundid || filters.roundid },
          dataOut: FIRST_PAGE,
          loading: false,
        });
      })
      .catch((err) => {
        console.log("Error: ", err);
        Auth.handleHttpCodes(err, this.props.name);
        if (this.mounted && request === this.requestId) this.setState({ loading: false });
      });
  };

  handleRoundSelect = (e) => {
    const filters = { ...this.state.filters, roundid: e.target.value };
    this.setState({ filters });
    this.getReport(this.state.reportType, filters);
  };
  handleReportSelect = (e) => this.getReport(e.target.value, this.state.filters);
  handleSearch = (e) => this.setState({ filters: { ...this.state.filters, name: e.target.value }, dataOut: FIRST_PAGE });
  loadMore = () => this.setState((state) => ({ dataOut: state.dataOut + PAGE_STEP }));

  download = () => {
    downloadFile(
      "POST",
      link() + `&do=get&ra=report&rec=${this.state.reportType}` + `&ufile=getfile`,
      this.requestBody(this.state.filters),
      { "Content-Type": "application/application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
      this.props.downloadName
    );
  };

  summaryItems = (report, rows, searching, balanceLayout) => {
    const currency = report.currency || "";
    const money = (value) => `${currency} ${numb2(value)}`;
    const sum = (field) => rows.reduce((total, item) => total + toNumber(item[field]), 0);
    if (balanceLayout) {
      return [
        { label: "Payable", value: money(searching ? sum("capitalbalance") : report.capitalbalanceTotal) },
        { label: "Others", value: money(searching ? sum("penaltybalance") : report.penaltybalanceTotal) },
        { label: "Sum", value: money(searching ? sum("capitalbalance") + sum("penaltybalance") : report.totalbalance), highlight: true },
      ];
    }
    return [
      { label: "Total to pay", value: money(searching ? sum("topay") : report.topay) },
      { label: "Total paid", value: money(searching ? sum("paid") : report.paid) },
      { label: "Balance", value: money(searching ? sum("balance") : report.balance), highlight: true },
    ];
  };

  render() {
    const { title, partyLabel, reportTypes, balanceTypes } = this.props;
    const { rounds, report, reportType, filters, loading, dataOut } = this.state;
    const balanceLayout = balanceTypes.includes(reportType);
    const allRows = report && Array.isArray(report.attachment) ? report.attachment : [];
    const searching = filters.name.trim() !== "";
    const rows = allRows.filter((item) => matchesSearch(item.names, filters.name));
    const visible = rows.slice(0, dataOut);
    const columns = withParty(balanceLayout ? BALANCE_COLUMNS : STANDARD_COLUMNS, partyLabel);

    return (
      <ReportLayout>
        <ReportCard
          title={title}
          subtitle={report && report.roundName}
          onDownload={allRows.length > 0 ? this.download : undefined}
          downloadLabel={`Download ${title.replace(":", "").toLowerCase()}`}
        >
          <ReportFilters>
            <RoundSelect rounds={rounds} value={filters.roundid} onChange={this.handleRoundSelect} />
            <ReportField label="Report">
              <select value={reportType} onChange={this.handleReportSelect} aria-label="Select report">
                {reportTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </ReportField>
            <SearchField label={partyLabel} value={filters.name} onChange={this.handleSearch} />
          </ReportFilters>

          {loading && <Loading />}
          {!loading && !report && <ReportEmpty>The report could not be loaded.</ReportEmpty>}
          {!loading && report && (
            <>
              <ReportSummary items={this.summaryItems(report, rows, searching, balanceLayout)} />
              {rows.length === 0 ? (
                <ReportEmpty>{allRows.length === 0 ? "There is nothing to report for this selection." : "No lines match the search."}</ReportEmpty>
              ) : (
                <>
                  <ReportTable columns={columns}>
                    {visible.map((item, index) => (
                      <tr key={`${item.itemid}-${index}`}>
                        <td>{index + 1}</td>
                        <td>{item.itemid}</td>
                        <td className="nowrap">{item.month}</td>
                        <td>{item.names}</td>
                        <td>{item.category}</td>
                        <td>{item.type}</td>
                        <td className="wide">{item.description}</td>
                        <td>{item.currency}</td>
                        {balanceLayout ? (
                          <>
                            <td className="num">{numb2(item.capitalbalance)}</td>
                            <td className="num">{numb2(item.penaltybalance)}</td>
                          </>
                        ) : (
                          <>
                            <td className="nowrap">{(item.paiddate || "").toString().substring(0, 10)}</td>
                            <td className="num">{numb2(item.topay)}</td>
                            <td className="num">{numb2(item.paid)}</td>
                            <td className="num">{numb2(item.balance)}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </ReportTable>
                  <ReportFooter shown={visible.length} total={rows.length} onMore={this.loadMore} />
                </>
              )}
            </>
          )}
        </ReportCard>
      </ReportLayout>
    );
  }
}

export default LedgerReport;
